import { useEffect, useRef } from 'react';
import { Square } from 'lucide-react';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';

/**
 * VoiceRecordingBar
 * -----------------
 * Inline panel rendered above the textarea while voice input is active.
 *
 * The waveform is a cosmetic animation — not driven by real mic
 * amplitude. We can't run a Web Audio level meter alongside
 * SpeechRecognition without grabbing the mic twice, which causes
 * Chrome's mic indicator to flap and the recogniser to fail
 * intermittently. Instead the bars run a slow sinusoidal "idle"
 * pattern while listening, and surge to a higher amplitude for ~600ms
 * whenever the recogniser commits any new (interim) text — so the
 * visual feedback IS tied to actual speech, just indirectly.
 *
 * Visual reference: Claude / ChatGPT voice — premium, calm, gives
 * unambiguous feedback that the mic is hot.
 */
interface VoiceRecordingBarProps {
  isSpeaking: boolean;
  interim: string;
  onStop: () => void;
}

const BAR_COUNT = 24;

export function VoiceRecordingBar({ isSpeaking, interim, onStop }: VoiceRecordingBarProps) {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  // The state ref lets the rAF loop read the latest isSpeaking without
  // having to re-bind through React on every change.
  const isSpeakingRef = useRef(isSpeaking);
  isSpeakingRef.current = isSpeaking;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Skip the animated waveform for reduced-motion users — the bars stay at
    // their static resting height; the red dot + "Listening…" copy still say
    // the mic is hot.
    if (reducedMotion) return;
    const tick = (now: number) => {
      if (startedAtRef.current === null) startedAtRef.current = now;
      const t = (now - startedAtRef.current) / 1000;
      const speaking = isSpeakingRef.current;
      for (let i = 0; i < BAR_COUNT; i += 1) {
        const el = barsRef.current[i];
        if (!el) continue;
        // Each bar gets a slight phase offset for a "wave" travelling
        // across the row. Frequency + amplitude differ between idle and
        // speaking — quiet ripple vs. lively dance.
        const phase = i * 0.32;
        const freq = speaking ? 7.5 : 1.6;
        const amplitudeBase = speaking ? 0.55 : 0.12;
        const amplitudeVar = speaking ? 0.45 : 0.08;
        const wave = (Math.sin(t * freq + phase) + 1) / 2; // 0..1
        const v = Math.min(1, amplitudeBase + wave * amplitudeVar);
        const h = 3 + v * 19;
        el.style.height = `${h}px`;
        el.style.opacity = String(0.5 + v * 0.5);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedAtRef.current = null;
    };
  }, [reducedMotion]);

  return (
    <div className="flex items-center gap-3 w-full px-3 py-2 rounded-xl bg-primary/10 border border-primary/20">
      {/* Pulsing red dot — universal "recording" affordance. */}
      <span className="relative flex items-center justify-center w-2 h-2 shrink-0">
        <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75 motion-reduce:animate-none" />
        <span className="relative w-2 h-2 rounded-full bg-red-500" />
      </span>

      {/* Cosmetic waveform — 24 bars. Surges when interim text arrives. */}
      <div className="flex items-center gap-[2px] h-[24px] shrink-0" aria-hidden>
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <span
            key={i}
            ref={(el) => {
              barsRef.current[i] = el;
            }}
            className="block w-[2px] rounded-full bg-primary transition-[height] duration-[80ms] ease-out"
            style={{ height: '3px', opacity: 0.5 }}
          />
        ))}
      </div>

      {/* Status / interim transcript. */}
      <p className="flex-1 min-w-0 text-sm text-foreground truncate">
        {interim ? (
          <>
            <span className="text-primary">“</span>
            {interim}
            <span className="text-primary animate-pulse motion-reduce:animate-none">▍</span>
          </>
        ) : (
          <span className="text-muted-foreground italic">Listening… speak naturally</span>
        )}
      </p>

      <button
        type="button"
        onClick={onStop}
        className="shrink-0 flex items-center justify-center h-7 w-7 rounded-full bg-primary hover:bg-primary/90 transition-colors shadow-sm"
        aria-label="Stop recording"
        title="Stop recording"
      >
        <Square className="w-3 h-3 text-primary-foreground fill-primary-foreground" />
      </button>
    </div>
  );
}
